import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { LucideIconData, MapPin } from 'lucide-angular';

import { PLAYERS_PROFILE_PATH } from '../../../../shared/players/players.constants';
import { PLAYERS_COPY } from '../../../../shared/players/players.copy';
import type { PlayerCard } from '../../../../shared/players/players.model';
import {
  aliasInitials,
  formatListedSince,
  playerLocationLabel,
} from '../../../../shared/players/players.utils';
import { IconComponent } from '../../../../shared/ui/icon/icon.component';

/**
 * Tarjeta de un jugador del directorio (B7, D6-D7 del spec web). Solo lo que emite la
 * API: alias (nunca el nombre), zona, juegos, presentación y desde cuándo está. Sin
 * «Activo hace» (B7) ni mascota (regla de la casa: nunca en tarjetas).
 *
 * La presentación se interpola como texto: ni `[innerHTML]`, ni autolink, ni markdown
 * (§5.6 4 bis del spec de la API; es la barrera real contra los enlaces).
 */
@Component({
  selector: 'app-player-card',
  imports: [IconComponent, RouterLink],
  templateUrl: './player-card.component.html',
  styleUrl: './player-card.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class PlayerCardComponent {
  readonly card = input.required<PlayerCard>();
  /** El directorio lo activa (J3, D32); sin él, el botón va deshabilitado. */
  readonly inviteEnabled = input(false);
  /** Solo emite con `inviteEnabled` (J3). */
  readonly invite = output<PlayerCard>();

  protected readonly copy = PLAYERS_COPY.card;
  protected readonly profilePath = PLAYERS_PROFILE_PATH;
  protected readonly mapPinIcon: LucideIconData = MapPin;

  protected readonly initials = computed(() => aliasInitials(this.card().alias));
  protected readonly location = computed(() => playerLocationLabel(this.card()));
  protected readonly since = computed(() => formatListedSince(this.card().listed_since));
  protected readonly bio = computed(() => this.card().bio?.trim() ?? '');

  protected onInvite(): void {
    if (this.inviteEnabled()) this.invite.emit(this.card());
  }
}
